"use client";

import { HeaderDailyNavLink } from "@/components/layout/header-daily-nav-link";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { showDailyPuzzleNavLink } from "@/lib/constants/daily-puzzle-nav";
import { Shield, SignOut } from "@phosphor-icons/react";
import type { User } from "@supabase/supabase-js";
import Link from "next/link";
import { usePathname } from "next/navigation";

// Custom 2x2 Grid Icon with tighter spacing and subtle rounding
const GridIcon = ({ className = "h-5 w-5" }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    className={className}
    fill="currentColor"
    aria-hidden="true"
  >
    <rect x="3" y="3" width="8" height="8" rx="1.5" />
    <rect x="13" y="3" width="8" height="8" rx="1.5" />
    <rect x="3" y="13" width="8" height="8" rx="1.5" />
    <rect x="13" y="13" width="8" height="8" rx="1.5" />
  </svg>
);

interface HeaderMobileNavProps {
  user: User | null;
  isAdmin: boolean;
  isLoading: boolean;
  handleLogout: () => Promise<void>;
  getUserInitials: () => string;
}

function AuthenticatedMobileNav({
  user,
  isAdmin,
  handleLogout,
  getUserInitials,
  pathname,
}: {
  user: User;
  isAdmin: boolean;
  handleLogout: () => Promise<void>;
  getUserInitials: () => string;
  pathname: string;
}) {
  return (
    <nav aria-label="Mobile navigation" className="space-y-6">
      {/* User info with avatar */}
      <div className="border-b border-gray-700 pb-4 flex items-center gap-3">
        <Avatar className="h-10 w-10 bg-brand-purple/90 text-white">
          <AvatarFallback className="bg-brand-purple text-white font-medium">
            {getUserInitials()}
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="text-sm text-gray-500">Signed in as:</p>
          <p className="font-medium truncate">{user.email}</p>
        </div>
      </div>

      <div className="space-y-4">
        <Link
          href="/"
          className="flex items-center py-2 px-4 hover:bg-gray-800 rounded-lg transition-colors duration-200 motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-current={pathname === "/" ? "page" : undefined}
        >
          <GridIcon className="h-5 w-5 mr-2" />
          Games
        </Link>

        <Link
          href="/about"
          className="block py-2 px-4 hover:bg-gray-800 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-current={pathname === "/about" ? "page" : undefined}
        >
          About
        </Link>

        <Link
          href="/partners"
          className="block py-2 px-4 hover:bg-gray-800 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-current={pathname === "/partners" ? "page" : undefined}
        >
          Partners
        </Link>
        <Link
          href="/educators"
          className="block py-2 px-4 hover:bg-gray-800 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-current={pathname === "/educators" ? "page" : undefined}
        >
          Educators
        </Link>
        {showDailyPuzzleNavLink && (
          <HeaderDailyNavLink className="block py-2 px-4 hover:bg-gray-800 rounded-lg transition-colors duration-200 motion-reduce:transition-none focus-visible:ring-offset-gray-900" />
        )}

        {isAdmin && (
          <Link
            href="/admin"
            className="flex items-center py-2 px-4 text-brand-purple hover:bg-gray-800 rounded-lg transition-colors duration-200 motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-current={pathname === "/admin" ? "page" : undefined}
          >
            <Shield
              weight="fill"
              className="h-5 w-5 mr-2 fill-current"
              strokeWidth={1.5}
              aria-hidden="true"
            />
            Admin Dashboard
          </Link>
        )}

        <button
          onClick={handleLogout}
          className="flex w-full items-center py-2 px-4 text-red-400 hover:bg-gray-800 rounded-lg transition-colors duration-200 motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <SignOut weight="fill" className="h-5 w-5 mr-2" aria-hidden="true" />
          Log out
        </button>
      </div>
    </nav>
  );
}

function UnauthenticatedMobileNav({ pathname }: { pathname: string }) {
  return (
    <nav aria-label="Mobile navigation" className="space-y-6 flex-1 flex flex-col">
      <div className="space-y-4 flex-1">
        <Link
          href="/about"
          className="block py-2 px-4 hover:bg-gray-800 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-current={pathname === "/about" ? "page" : undefined}
        >
          About
        </Link>
        <Link
          href="/partners"
          className="block py-2 px-4 hover:bg-gray-800 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-current={pathname === "/partners" ? "page" : undefined}
        >
          Partners
        </Link>
        <Link
          href="/educators"
          className="block py-2 px-4 hover:bg-gray-800 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-current={pathname === "/educators" ? "page" : undefined}
        >
          Educators
        </Link>
        {showDailyPuzzleNavLink && (
          <HeaderDailyNavLink className="block py-2 px-4 hover:bg-gray-800 rounded-lg transition-colors duration-200 motion-reduce:transition-none focus-visible:ring-offset-gray-900" />
        )}
        <Link
          href="/games"
          className="block py-2 px-4 hover:bg-gray-800 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-current={pathname === "/games" ? "page" : undefined}
        >
          Games
        </Link>
      </div>

      {/* Add Play Games button for unauthenticated users */}
      <div className="pt-6 border-t border-gray-700">
        <Link
          href="/games"
          className="block py-2 px-4 text-center bg-white text-black rounded-lg hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          See Games
        </Link>
      </div>
    </nav>
  );
}

export function HeaderMobileNav({
  user,
  isAdmin,
  isLoading,
  handleLogout,
  getUserInitials,
}: HeaderMobileNavProps) {
  const pathname = usePathname();

  if (isLoading) {
    return (
      <div className="flex justify-center py-8" aria-live="polite">
        <div
          className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-purple"
          aria-hidden="true"
        />
        <span className="sr-only">Loading navigation menu</span>
      </div>
    );
  }

  if (user) {
    return (
      <AuthenticatedMobileNav
        user={user}
        isAdmin={isAdmin}
        handleLogout={handleLogout}
        getUserInitials={getUserInitials}
        pathname={pathname}
      />
    );
  }

  return <UnauthenticatedMobileNav pathname={pathname} />;
}
